import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { styled } from '@mui/system'
import { Box, Button, Collapse, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const IdPortenIcon = () => (
  <svg width="26" height="25" fill="none" viewBox="0 0 26 25" aria-hidden="true">
    <path
      fill="currentColor"
      stroke="currentColor"
      d="M1 8.62h6.092v1.083l-.738.902v.542l4.246 5.955-4.246 5.774.738.722v.902H1v-4.33l1.292-.903v-4.872L1 13.673zM25 8.62h-6.092v1.083l.738.902v.542L15.4 17.102l4.246 5.774-.738.722v.902H25v-4.33l-1.292-.903v-4.872L25 13.673zM1.923.5l-.738.541v5.053H24.63V1.041L23.892.5h-3.507v2.346h-5.17V.5h-4.8v2.346H5.062V.5z"
    />
  </svg>
)

const FeideIcon = () => (
  <svg width="24" height="25" fill="none" viewBox="0 0 24 25" aria-hidden="true">
    <path
      fill="currentColor"
      d="M20 13.25h-2.63v6.38H20zM11.316 15.11H8.684v7.177H2.632V13.25H0v9.568h.013v2.126H20v-2.657h-8.684zM10 12.453c.872 0 1.579-.714 1.579-1.595 0-.88-.707-1.595-1.58-1.595-.871 0-1.578.714-1.578 1.595 0 .88.707 1.595 1.579 1.595"
    />
    <path
      fill="currentColor"
      d="M10 3.158c4.06 0 7.362 3.335 7.362 7.435h2.631C19.993 5.026 15.511.5 10 .5 4.49.5.008 5.027.008 10.593h2.634c0-4.1 3.3-7.435 7.358-7.435"
    />
  </svg>
)

const ArrowRightIcon = () => (
  <svg width="24" height="24" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
      d="M5 12h14M12 5l7 7-7 7"
    />
  </svg>
)

const Card = styled(Box)({
  borderStyle: 'solid',
  borderColor: 'black',
  borderWidth: '0.125rem',
  borderRadius: '0.6875rem',
  padding: '1.5rem 2rem',
})

interface GuestLoginOptionsProps {
  // Invoked just before navigating to the OIDC login endpoint, so the caller
  // can set the post-login redirect cookie. Fired for both login options.
  onClickLogin?: () => void
}

/*
 * Login options shown to an invited guest who is not yet authenticated.
 *
 * The options are ranked: ID-porten (highest-assurance identity) first, Feide
 * second, and manual registration tucked behind a disclosure as a deliberate
 * last resort. The login_hint query parameter routes each button past Feide's
 * discovery page straight to the intended provider.
 */
const GuestLoginOptions = ({
  onClickLogin = () => {},
}: GuestLoginOptionsProps) => {
  const { t } = useTranslation('login')
  const [manualOpen, setManualOpen] = useState(false)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <Typography variant="body1">{t('intro')}</Typography>

      {/* Option 1: ID-porten (primary, highest assurance) */}
      <Card>
        <Typography
          variant="h3"
          component="h2"
          sx={{ marginBottom: '1rem' }}
        >
          {t('idporten.question')}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          size="large"
          href="/oidc/authenticate/?login_hint=idporten"
          onClick={onClickLogin}
          startIcon={<IdPortenIcon />}
          sx={{ textTransform: 'none' }}
        >
          {t('idporten.button')}
        </Button>
        <Typography variant="body2" sx={{ marginTop: '0.75rem' }}>
          {t('idporten.hint')}
        </Typography>
      </Card>

      {/* Option 2: Feide at another institution (secondary) */}
      <Card>
        <Typography
          variant="h3"
          component="h2"
          sx={{ marginBottom: '1rem' }}
        >
          {t('feide.question')}
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          href="/oidc/authenticate/?login_hint=feide%7Call"
          onClick={onClickLogin}
          startIcon={<FeideIcon />}
          sx={{ textTransform: 'none' }}
        >
          {t('feide.button')}
        </Button>
      </Card>

      {/* Option 3: Manual registration (last resort, behind a disclosure) */}
      <Box>
        <Button
          variant="text"
          color="secondary"
          onClick={() => setManualOpen((open) => !open)}
          endIcon={
            <ExpandMoreIcon
              sx={{
                transition: 'transform 150ms',
                transform: manualOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          }
          aria-expanded={manualOpen}
          sx={{
            paddingX: 0,
            fontSize: '1rem',
            textTransform: 'none',
            textDecoration: 'underline',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {t('manualOption.toggle')}
        </Button>
        <Collapse in={manualOpen}>
          <Box sx={{ paddingTop: '1rem' }}>
            <Typography variant="body2" sx={{ marginBottom: '1rem' }}>
              {t('manualOption.info')}
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              href="/guestregister/"
              endIcon={<ArrowRightIcon />}
              sx={{ textTransform: 'none' }}
            >
              {t('manualOption.button')}
            </Button>
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
}

GuestLoginOptions.defaultProps = { onClickLogin: undefined }

export default GuestLoginOptions
